import { useState } from 'react'
import {
  ModelReconciliationRequest,
  type TModelReconciliationRequest,
  type TModelReconciliationInventory,
  type TModelReconciliationPreview,
  type TModelReconciliationResult,
} from '@schemas'
import {
  useModelReconciliation,
  usePreviewModelReconciliation,
  useApplyModelReconciliation,
} from '@/api/hooks'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

type Row = TModelReconciliationInventory['rows'][number]
const blank = () => ModelReconciliationRequest.parse({})
const inputStyle = 'w-full rounded-md border bg-background p-2 text-sm'
function download(name: string, value: unknown) {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' })
  )
  const link = document.createElement('a')
  link.href = url
  link.download = name
  link.click()
  URL.revokeObjectURL(url)
}

export default function ModelReconciliationTools() {
  const [open, setOpen] = useState(false)
  return (
    <section
      className="space-y-4 rounded-lg border p-4"
      aria-label="Historical model reconciliation"
    >
      <h2 className="text-lg font-semibold">Historical model reconciliation</h2>
      <p>
        Review legacy model links and series history. Existing settings and published reports are
        preserved.
      </p>
      <Button onClick={() => setOpen(!open)}>
        {open ? 'Close reconciliation' : 'Open reconciliation'}
      </Button>
      {open && <Workspace />}
    </section>
  )
}

function Workspace() {
  const query = useModelReconciliation()
  const previewMutation = usePreviewModelReconciliation()
  const applyMutation = useApplyModelReconciliation()
  const [draft, setDraft] = useState<TModelReconciliationRequest>(blank)
  const [preview, setPreview] = useState<TModelReconciliationPreview | null>(null)
  const [receipt, setReceipt] = useState<TModelReconciliationResult | null>(null)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [kind, setKind] = useState('class')
  const [page, setPage] = useState(0)
  const busy = previewMutation.isPending || applyMutation.isPending
  const change = (next: TModelReconciliationRequest) => {
    setDraft(next)
    setPreview(null)
    setReceipt(null)
    setError('')
  }
  const data = query.data
  if (query.isPending) return <p>Loading complete inventory…</p>
  if (!data) return <p role="alert">{query.error?.message ?? 'Could not load inventory.'}</p>
  const models = [
    ...data.rows
      .filter(r => r.entityType === 'model')
      .map(r => ({ id: r.id, name: r.label, retired: r.retired })),
    ...draft.historicalModels,
  ]
  const subtypes = [
    ...data.rows
      .filter(r => r.entityType === 'subtype')
      .map(r => ({
        id: r.id,
        name: r.label,
        catalogClassId: r.catalogClassId!,
        retired: r.retired,
      })),
    ...draft.historicalSubtypes,
  ]
  const rows = data.rows.filter(
    r => r.entityType === kind && `${r.label} ${r.id}`.toLowerCase().includes(search.toLowerCase())
  )
  const selected = Object.values(draft).reduce((sum, values) => sum + values.length, 0)
  async function review() {
    setError('')
    setPreview(null)
    try {
      setPreview(await previewMutation.mutateAsync(draft))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not preview changes.')
    }
  }
  async function apply() {
    if (!preview) return
    setError('')
    try {
      const result = await applyMutation.mutateAsync({
        ...draft,
        reviewToken: preview.reviewToken,
        reviewedAt: preview.reviewedAt,
      })
      setReceipt(result)
      setDraft(blank())
      setPreview(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not apply changes.')
      setPreview(null)
    }
  }
  function queue(
    row: Row,
    value: {
      catalogClassId: string
      catalogSubtypeId: string | null
      divisionCode: string
      reason: string
      retired: boolean
    }
  ) {
    const { catalogClassId, catalogSubtypeId, reason, divisionCode, retired } = value
    if (row.entityType === 'class')
      change({
        ...draft,
        classes: [
          ...draft.classes.filter(c => c.classId !== row.id),
          { classId: row.id, catalogClassId, catalogSubtypeId, reason },
        ],
      })
    else if (row.entityType === 'identity')
      change({
        ...draft,
        identities: [
          ...draft.identities.filter(i => i.identityId !== row.id),
          { identityId: row.id, catalogClassId, catalogSubtypeId, divisionCode, reason, retired },
        ],
      })
    else
      change({
        ...draft,
        catalog: [
          ...draft.catalog.filter(c => !(c.entityId === row.id && c.entityType === row.entityType)),
          {
            entityType: row.entityType,
            entityId: row.id,
            catalogClassId,
            catalogSubtypeId,
            reason,
            retired,
          },
        ],
      })
  }
  return (
    <div className="space-y-5">
      <p>
        {data.rows.length} records accounted for. {data.problems.length} unresolved checks across
        all records. Constraints: {data.enforced ? 'active' : 'awaiting operator activation'}.
      </p>
      <div className="flex flex-wrap gap-3">
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => {
            setPreview(null)
            void query.refetch()
          }}
        >
          Reload inventory
        </Button>
        <Button
          variant="outline"
          onClick={() => download('model-reconciliation-inventory.json', data)}
        >
          Download inventory
        </Button>
      </div>
      <details>
        <summary>All unresolved checks ({data.problems.length})</summary>
        <ul>
          {data.problems.map((p, i) => (
            <li key={i}>
              {p.entityType} {p.entityId}: {p.message}
            </li>
          ))}
        </ul>
      </details>
      {(data.warnings?.length ?? 0) > 0 && (
        <details open>
          <summary>Retained competing histories</summary>
          <ul>
            {data.warnings.map((p, i) => (
              <li key={i}>{p.message}</li>
            ))}
          </ul>
        </details>
      )}
      <fieldset disabled={busy} className="space-y-4">
        <legend className="font-semibold">Select records to review</legend>
        <label className="block">
          Record type
          <select
            className={inputStyle}
            value={kind}
            onChange={e => {
              setKind(e.target.value)
              setPage(0)
            }}
          >
            <option value="class">Show classes</option>
            <option value="identity">Series identities</option>
            <option value="model">Catalog models</option>
            <option value="subtype">Catalog subtypes</option>
          </select>
        </label>
        <label className="block">
          Find record
          <Input
            value={search}
            onChange={e => {
              setSearch(e.target.value)
              setPage(0)
            }}
          />
        </label>
        {rows.slice(page * 25, (page + 1) * 25).map(row => (
          <RecordEditor
            key={`${row.entityType}:${row.id}`}
            row={row}
            models={models}
            subtypes={subtypes}
            problems={data.problems
              .filter(p => p.entityType === row.entityType && p.entityId === row.id)
              .map(p => p.message)}
            onQueue={value => queue(row, value)}
          />
        ))}
        <div className="flex gap-3">
          <Button variant="outline" disabled={!page} onClick={() => setPage(page - 1)}>
            Previous records
          </Button>
          <span>{rows.length} matching records</span>
          <Button
            variant="outline"
            disabled={(page + 1) * 25 >= rows.length}
            onClick={() => setPage(page + 1)}
          >
            Next records
          </Button>
        </div>
        <HistoricalEditor
          models={models}
          onModel={model =>
            change({ ...draft, historicalModels: [...draft.historicalModels, model] })
          }
          onSubtype={subtype =>
            change({ ...draft, historicalSubtypes: [...draft.historicalSubtypes, subtype] })
          }
        />
        <DecisionEditor
          data={data}
          onQueue={decision =>
            change({
              ...draft,
              decisions: [...draft.decisions.filter(d => d.classId !== decision.classId), decision],
            })
          }
        />
      </fieldset>
      <h3 className="font-semibold">
        Pending review ({selected} records, including other filters and pages)
      </h3>
      {selected > 0 && (
        <>
          <details open>
            <summary>All selected mappings and reasons</summary>
            <pre className="max-h-96 overflow-auto whitespace-pre-wrap text-xs">
              {JSON.stringify(draft, null, 2)}
            </pre>
          </details>
          <div className="flex gap-3">
            <Button disabled={busy} onClick={() => void review()}>
              Preview reconciliation
            </Button>
            <Button variant="outline" disabled={busy} onClick={() => change(blank())}>
              Discard draft
            </Button>
          </div>
        </>
      )}
      {error && <p role="alert">{error} Your draft has been retained.</p>}
      {preview && (
        <section className="space-y-3" aria-label="Reconciliation preview">
          <h3 className="font-semibold">Review every proposed change</h3>
          <p>
            {preview.changes.length} changes. {preview.remaining.length} unresolved checks will
            remain. Existing defaults and publication payloads are not replaced.
          </p>
          {preview.blockers.length > 0 && (
            <ul role="alert">
              {preview.blockers.map((p, i) => (
                <li key={i}>
                  {p.entityId}: {p.message}
                </li>
              ))}
            </ul>
          )}
          {(preview.warnings?.length ?? 0) > 0 && (
            <ul>
              {preview.warnings.map((p, i) => (
                <li key={i}>{p.message}</li>
              ))}
            </ul>
          )}
          {preview.changes.map(c => (
            <details key={`${c.entityType}:${c.entityId}`}>
              <summary>
                {c.entityType} {c.entityId}: {c.reason}
              </summary>
              <pre className="max-h-96 overflow-auto whitespace-pre-wrap text-xs">
                {JSON.stringify({ before: c.before, after: c.after }, null, 2)}
              </pre>
            </details>
          ))}
          <Button
            variant="outline"
            onClick={() =>
              download('model-reconciliation-review.json', { request: draft, preview })
            }
          >
            Download reviewed changes
          </Button>{' '}
          <Button disabled={busy || !preview.canApply} onClick={() => void apply()}>
            Apply reviewed reconciliation
          </Button>
        </section>
      )}
      {receipt && (
        <div role="status">
          Reconciliation saved: {receipt.changed} reviewed records. {receipt.remaining.length}{' '}
          unresolved checks remain.{' '}
          <Button
            variant="outline"
            onClick={() => download('model-reconciliation-receipt.json', receipt)}
          >
            Download receipt
          </Button>
        </div>
      )}
      <p>
        Database enforcement is a separate operator step after the complete unresolved report is
        clear.
      </p>
    </div>
  )
}

type Choices = {
  models: { id: string; name: string; retired: boolean }[]
  subtypes: { id: string; catalogClassId: string; name: string; retired: boolean }[]
}
function RecordEditor({
  row,
  models,
  subtypes,
  problems,
  onQueue,
}: Choices & {
  row: Row
  problems: string[]
  onQueue: (v: {
    catalogClassId: string
    catalogSubtypeId: string | null
    divisionCode: string
    reason: string
    retired: boolean
  }) => void
}) {
  const [model, setModel] = useState(row.catalogClassId ?? '')
  const [subtype, setSubtype] = useState(row.catalogSubtypeId ?? '')
  const [division, setDivision] = useState(row.divisionCode ?? '')
  const [reason, setReason] = useState('')
  const [retired, setRetired] = useState(row.retired)
  const choices = subtypes.filter(s => s.catalogClassId === model)
  return (
    <details className="rounded border p-3">
      <summary>
        {row.label} — {row.id}
        {problems.length ? ` (${problems.length} unresolved)` : ''}
      </summary>
      <div className="mt-3 space-y-3">
        {problems.map(p => (
          <p key={p}>{p}</p>
        ))}
        <label className="block">
          Target model
          <select
            aria-label={`Target model for ${row.id}`}
            className={inputStyle}
            value={model}
            onChange={e => {
              setModel(e.target.value)
              setSubtype('')
            }}
          >
            <option value="">Choose model</option>
            {models.map(m => (
              <option key={m.id} value={m.id}>
                {m.name} {m.retired ? '(retired)' : ''} — {m.id}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          Target subtype
          <select
            aria-label={`Target subtype for ${row.id}`}
            className={inputStyle}
            value={subtype}
            onChange={e => setSubtype(e.target.value)}
          >
            <option value="">No subtype</option>
            {choices.map(s => (
              <option key={s.id} value={s.id}>
                {s.name} {s.retired ? '(retired)' : ''} — {s.id}
              </option>
            ))}
          </select>
        </label>
        {row.entityType === 'identity' && (
          <label className="block">
            Canonical division code
            <Input value={division} onChange={e => setDivision(e.target.value)} />
          </label>
        )}
        {row.entityType !== 'class' && (
          <label className="flex gap-2">
            <input type="checkbox" checked={retired} onChange={e => setRetired(e.target.checked)} />
            Retire this original record
          </label>
        )}
        <label className="block">
          Review reason
          <Input
            aria-label={`Review reason for ${row.id}`}
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        </label>
        <details>
          <summary>Current record and preserved settings</summary>
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap text-xs">
            {JSON.stringify(row.values, null, 2)}
          </pre>
        </details>
        <Button
          disabled={!model || !reason.trim() || (row.entityType === 'identity' && !division.trim())}
          onClick={() =>
            onQueue({
              catalogClassId: model,
              catalogSubtypeId: subtype || null,
              divisionCode: division,
              reason,
              retired,
            })
          }
        >
          Add to reconciliation review
        </Button>
      </div>
    </details>
  )
}

function HistoricalEditor({
  models,
  onModel,
  onSubtype,
}: Pick<Choices, 'models'> & {
  onModel: (v: TModelReconciliationRequest['historicalModels'][number]) => void
  onSubtype: (v: TModelReconciliationRequest['historicalSubtypes'][number]) => void
}) {
  const [kind, setKind] = useState('model')
  const [name, setName] = useState('')
  const [category, setCategory] = useState('')
  const [model, setModel] = useState('')
  const [reason, setReason] = useState('')
  return (
    <details className="rounded border p-3">
      <summary>Create a reviewed historical model or subtype</summary>
      <div className="space-y-3">
        <p>
          Use only when no defensible existing equivalent exists. Historical records start retired,
          with unknown source provenance and no invented adoption date.
        </p>
        <label className="block">
          Historical record type
          <select className={inputStyle} value={kind} onChange={e => setKind(e.target.value)}>
            <option value="model">Model</option>
            <option value="subtype">Subtype</option>
          </select>
        </label>
        <label className="block">
          Historical name
          <Input value={name} onChange={e => setName(e.target.value)} />
        </label>
        {kind === 'model' ? (
          <label className="block">
            Existing category code
            <Input value={category} onChange={e => setCategory(e.target.value)} />
          </label>
        ) : (
          <label className="block">
            Parent model
            <select className={inputStyle} value={model} onChange={e => setModel(e.target.value)}>
              <option value="">Choose model</option>
              {models.map(m => (
                <option key={m.id} value={m.id}>
                  {m.name} — {m.id}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="block">
          Historical evidence or review reason
          <Input value={reason} onChange={e => setReason(e.target.value)} />
        </label>
        <Button
          disabled={!name.trim() || !reason.trim() || !(kind === 'model' ? category.trim() : model)}
          onClick={() => {
            const base = {
              id: crypto.randomUUID(),
              name,
              description: null,
              retired: true as const,
              reason,
            }
            if (kind === 'model') onModel({ ...base, categoryCode: category })
            else onSubtype({ ...base, catalogClassId: model })
            setName('')
            setReason('')
          }}
        >
          Add historical record to review
        </Button>
      </div>
    </details>
  )
}

function DecisionEditor({
  data,
  onQueue,
}: {
  data: TModelReconciliationInventory
  onQueue: (v: TModelReconciliationRequest['decisions'][number]) => void
}) {
  const [classId, setClassId] = useState('')
  const [identityId, setIdentityId] = useState('')
  const [division, setDivision] = useState('')
  const [reason, setReason] = useState('')
  const cls = data.rows.find(r => r.entityType === 'class' && r.id === classId)
  return (
    <details className="rounded border p-3">
      <summary>Review a conflicting series assignment</summary>
      <div className="space-y-3">
        <label className="block">
          Series class
          <select
            className={inputStyle}
            value={classId}
            onChange={e => {
              setClassId(e.target.value)
              setIdentityId('')
              setDivision('')
            }}
          >
            <option value="">Choose class</option>
            {data.rows
              .filter(r => r.entityType === 'class' && r.seriesId)
              .map(r => (
                <option key={r.id} value={r.id}>
                  {r.label} — {r.id}
                </option>
              ))}
          </select>
        </label>
        <label className="block">
          Series assignment
          <select
            className={inputStyle}
            value={identityId}
            onChange={e => {
              setIdentityId(e.target.value)
              setDivision('')
            }}
          >
            <option value="">Explicitly unmatched</option>
            {data.rows
              .filter(r => r.entityType === 'identity' && r.seriesId === cls?.seriesId)
              .map(r => (
                <option key={r.id} value={r.id}>
                  {r.label} — {r.id}
                </option>
              ))}
          </select>
        </label>
        {identityId && (
          <label className="block">
            Reviewed edition division code (only for an intentional difference)
            <Input value={division} onChange={e => setDivision(e.target.value)} />
          </label>
        )}
        <label className="block">
          Assignment review reason
          <Input value={reason} onChange={e => setReason(e.target.value)} />
        </label>
        <Button
          disabled={!classId || !reason.trim()}
          onClick={() =>
            onQueue({
              classId,
              identityId: identityId || null,
              reviewedDivisionCode: identityId ? division || null : null,
              reason,
            })
          }
        >
          Add assignment to review
        </Button>
      </div>
    </details>
  )
}
